import { and, eq } from "drizzle-orm";
import {
  cash_movement,
  payment,
  type PowerSyncDrizzleDB,
  register_session,
} from "@comandero/powersync";
import { liveCashMovementFilters, livePaymentFilters } from "../live/filters";
import { nowISO } from "../utils/date-helpers";
import { patchRowVerified, type VerifiedWriteOptions } from "../powersync/verified-write";
import { generateId } from "@fleet/utils";
import { reconcileCashbox } from "../orders/calc";

/** Opens a cashbox session with initial float (cents). */
export async function openRegisterSessionMutation(
  db: PowerSyncDrizzleDB,
  organizationId: string,
  branchId: string,
  openingFloat: number,
  cashierUserId?: string | null,
): Promise<string> {
  const id = generateId();
  const now = nowISO();
  await db.insert(register_session).values({
    id,
    organization_id: organizationId,
    branch_id: branchId,
    cashier_user_id: cashierUserId ?? null,
    opening_float: Math.trunc(openingFloat),
    status: "open",
    opened_at: now,
    created_at: now,
    updated_at: now,
  });
  return id;
}

/** Registers a cashbox movement (in/out/sale/refund). */
export async function addCashMovementMutation(
  db: PowerSyncDrizzleDB,
  organizationId: string,
  registerSessionId: string,
  kind: "in" | "out" | "sale" | "refund",
  amount: number,
  reason?: string,
): Promise<string> {
  const id = generateId();
  const now = nowISO();
  await db.insert(cash_movement).values({
    id,
    organization_id: organizationId,
    register_session_id: registerSessionId,
    kind,
    amount: Math.trunc(amount),
    reason: reason ?? null,
    created_at: now,
    updated_at: now,
  });
  return id;
}

const SIGN_BY_KIND: Record<string, number> = { in: 1, sale: 1, refund: -1, out: -1 };

/**
 * Closes cashbox session with counted cash. Calculates expected from
 * initial float + session cash payments + manual movements, and
 * persists count. Returns reconciliation (expected/counted/discrepancy).
 * Calculation is pure (calc.reconcileCashbox).
 */
export async function closeRegisterSessionMutation(
  db: PowerSyncDrizzleDB,
  organizationId: string,
  registerSessionId: string,
  countedTotal: number,
  options?: VerifiedWriteOptions,
) {
  const sessionRows = await db
    .select()
    .from(register_session)
    .where(eq(register_session.id, registerSessionId))
    .limit(1);
  const openingFloat = sessionRows[0]?.opening_float ?? 0;

  // Cash payments of session (sales).
  const cashPayments = await db
    .select()
    .from(payment)
    .where(
      and(
        eq(payment.register_session_id, registerSessionId),
        eq(payment.method, "cash"),
        ...livePaymentFilters(),
      ),
    );

  // Manual movements (in/out), excluding "sale" already
  // counted via payment to avoid duplicates.
  const movements = await db
    .select()
    .from(cash_movement)
    .where(
      and(eq(cash_movement.register_session_id, registerSessionId), ...liveCashMovementFilters()),
    );

  const signed: number[] = [];
  for (const p of cashPayments) {
    // Cash in drawer for a sale = charged amount + cash tip.
    signed.push((p.amount ?? 0) + (p.tip ?? 0));
  }
  for (const m of movements) {
    if (m.kind === "sale") {
      continue; // evitar doble conteo con payment
    }
    const sign = SIGN_BY_KIND[m.kind ?? "in"] ?? 1;
    signed.push((m.amount ?? 0) * sign);
  }

  const reconciliation = reconcileCashbox(openingFloat, signed, countedTotal);

  await patchRowVerified(
    db,
    register_session,
    register_session.id,
    registerSessionId,
    { counted_total: Math.trunc(countedTotal), status: "closed", closed_at: nowISO() },
    { ...options, scope: [{ column: register_session.organization_id, value: organizationId }] },
  );

  return reconciliation;
}
